import { Minus, Plus, FlipHorizontal2 } from 'lucide-react';

interface SelectionOpsPanelProps {
  hasSelection: boolean;
  /** True when the active marquee supports grow / shrink (rect /
   *  ellipse). Lasso / polygon / wand return false; the row is
   *  hidden in that case rather than rendered disabled. */
  canGrow: boolean;
  /** Grow / shrink in scene-space pixels (positive = grow, negative
   *  = shrink). Caller decides the step size; Tier 0 uses ±4px per
   *  click and ±16px on shift-click. */
  onGrow: (deltaPx: number) => void;
  /** Invert the current selection — every pixel inside becomes
   *  outside and vice versa. Bounded by the image rect. */
  onInvert?: () => void;
  /** Compact layout for the StatusBar popover (smaller chips, less
   *  vertical spacing). Defaults to the inline-panel layout. */
  compact?: boolean;
}

const GROW_STEP = 4;
const GROW_STEP_SHIFT = 16;

/** Shared selection-modify panel. Used in two places:
 *  1) Inline section inside `MarqueePanel` when the marquee tool is
 *     active.
 *  2) Inside the StatusBar selection-chip popover so the same ops
 *     are available regardless of which tool is selected.
 *
 *  Select All / Deselect aren't surfaced here — Ctrl+A and Esc
 *  cover them on desktop and the marquee tool itself is the natural
 *  way to make/clear selections. AI Select Subject lives with the
 *  marquee shape buttons (it CREATES a selection); this panel is
 *  for ops that MODIFY an existing selection. */
export function SelectionOpsPanel({
  hasSelection,
  canGrow,
  onGrow,
  onInvert,
  compact = false,
}: SelectionOpsPanelProps) {
  const enabled = 'border-border text-foreground hover:bg-accent';

  // Renders if there's anything to show. Grow/shrink hides for
  // shape types that don't support it (lasso/polygon currently);
  // invert works for any selection.
  if (!hasSelection) return null;
  const showGrowShrink = canGrow;
  const showInvert = !!onInvert;
  if (!showGrowShrink && !showInvert) return null;

  const labelCls = `${compact ? 'text-[11px]' : 'text-xs'} text-muted-foreground w-20 shrink-0`;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {showGrowShrink && (
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>Grow / Shrink</span>
          <button
            type="button"
            className={`p-1 rounded border ${enabled}`}
            onClick={(e) => onGrow(-(e.shiftKey ? GROW_STEP_SHIFT : GROW_STEP))}
            title="Shrink (hold Shift for ×4)"
            aria-label="Shrink selection"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={`p-1 rounded border ${enabled}`}
            onClick={(e) => onGrow(e.shiftKey ? GROW_STEP_SHIFT : GROW_STEP)}
            title="Grow (hold Shift for ×4)"
            aria-label="Grow selection"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {showInvert && (
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>Invert</span>
          <button
            type="button"
            className={`p-1 rounded border ${enabled}`}
            onClick={onInvert}
            title="Invert selection (Ctrl+Shift+I)"
            aria-label="Invert selection"
          >
            <FlipHorizontal2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
